import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { feedbackPlatformLabel, feedbackTypeLabel } from '../../lib/feedbackPosts/labels';
import { formatFeedbackTime } from '../../lib/feedbackPosts/formatTime';
import { updateFeedbackPostStatus, deleteFeedbackPost } from '../../lib/feedbackPosts/firestore';
import FeedbackPostExpandedContent from './FeedbackPostExpandedContent';
import EditFeedbackPostModal from './EditFeedbackPostModal';
import ConfirmDialog from './ConfirmDialog';

export default function CreatorFeedbackPostCard({
  post,
  isExpanded,
  onToggleExpand,
  onNavigate,
  showAllComments,
  onToggleShowAllComments,
  onPostDeleted,
}) {
  const { user } = useAuth();
  const [statusBusy, setStatusBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const preview =
    post.description.length > 200
      ? `${post.description.slice(0, 200).trim()}…`
      : post.description;

  const isOpen = post.status === 'open';
  const needsFeedback = isOpen && post.commentCount === 0;
  const lowReplies = isOpen && post.commentCount > 0 && post.commentCount <= 2;

  const handleHeaderClick = (e) => {
    if (e.target.closest('[data-author-block]') || e.target.closest('[data-no-collapse]')) return;
    onToggleExpand();
  };

  const handleHeaderKeyDown = (e) => {
    if (e.target.closest('[data-author-block]') || e.target.closest('[data-no-collapse]')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggleExpand();
    }
  };

  const goToProfile = () => {
    if (post.authorUsername && onNavigate) {
      onNavigate('u/' + post.authorUsername);
    }
  };

  const toggleClosed = async (e) => {
    e.stopPropagation();
    if (!user || post.authorId !== user.uid || statusBusy) return;
    setStatusBusy(true);
    try {
      await updateFeedbackPostStatus(
        post.id,
        user.uid,
        post.status === 'open' ? 'closed' : 'open'
      );
    } finally {
      setStatusBusy(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!user || post.authorId !== user.uid || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await deleteFeedbackPost(post.id, user.uid);
      onPostDeleted?.(post.id);
      setDeleteConfirmOpen(false);
    } catch (err) {
      console.error('Delete post failed:', err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const isAuthor = user && post.authorId === user.uid;

  return (
    <div
      className={[
        'w-full text-left rounded-lg border bg-white transition-colors duration-150',
        needsFeedback
          ? 'border-l-[3px] border-l-amber-400 border-y border-r border-slate-200'
          : 'border border-slate-200',
        !isOpen && 'opacity-90',
      ].join(' ')}
    >
      {/* Single post content: summary when collapsed, full body when expanded */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        className={[
          'cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-ihTeal/40 focus:ring-offset-2 rounded-t-lg',
          isExpanded ? 'hover:bg-slate-50/80' : 'hover:bg-slate-50/40 hover:border-slate-300 rounded-b-lg',
        ].join(' ')}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Collapse post' : 'Expand post'}
      >
        <div className="px-3 py-4 min-w-0 sm:px-5 sm:py-4">
          {/*
            Mobile: stack title (full width) then meta row so chips never squeeze the title.
            sm+: original side-by-side layout (desktop unchanged).
          */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
            <div className="min-w-0 w-full sm:flex-1 sm:min-w-0">
              {needsFeedback && (
                <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                    Needs feedback
                  </span>
                  <span className="min-w-0 text-xs text-slate-500">Be the first to respond</span>
                </div>
              )}

              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Looking for input on
              </p>
              <h2
                className={`mt-1 break-words font-semibold leading-snug text-slate-900 ${isExpanded ? 'text-lg sm:text-xl' : 'text-base sm:text-lg line-clamp-2'}`}
              >
                {post.title}
              </h2>
            </div>
            {/* Menu + chips + chevron: full-width wrap on mobile, right-aligned column on sm+ */}
            <div className="flex w-full min-w-0 shrink-0 flex-col gap-2 sm:w-auto sm:items-end">
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2 justify-start sm:justify-end">
                {isAuthor && (
                  <div className="relative" ref={menuRef} data-no-collapse>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen((prev) => !prev);
                      }}
                      className="min-h-10 min-w-10 inline-flex items-center justify-center rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-ihTeal/40 focus:ring-inset sm:min-h-0 sm:min-w-0"
                      aria-label="Post actions"
                      aria-expanded={menuOpen}
                      aria-haspopup="true"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                      </svg>
                    </button>
                    {menuOpen && (
                      <div
                        className="absolute left-0 top-full z-20 mt-1 w-40 rounded-lg border border-slate-200 bg-white py-1 shadow-lg sm:left-auto sm:right-0"
                        role="menu"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpen(false);
                            setEditModalOpen(true);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpen(false);
                            setDeleteConfirmOpen(true);
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <span className="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2.5 py-1 text-center text-[11px] font-semibold leading-tight text-slate-700 shadow-sm">
                  {feedbackTypeLabel(post.feedbackType)}
                </span>
                <span className="inline-flex max-w-full items-center rounded-full bg-ihTeal/15 px-2.5 py-1 text-left text-[11px] font-semibold leading-tight text-ihTeal-dark shadow-sm break-words sm:text-center">
                  {feedbackPlatformLabel(post.platform)}
                </span>
                <span
                  className={[
                    'inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-sm',
                    isOpen
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-100 text-slate-600',
                  ].join(' ')}
                >
                  {isOpen ? 'Open' : 'Closed'}
                </span>
              </div>
              <div className="flex justify-end sm:w-full">
                <span className="inline-flex min-h-10 min-w-10 items-center justify-center text-slate-400 transition-transform sm:min-h-0 sm:min-w-0" aria-hidden>
                  <svg
                    className={`h-6 w-6 sm:h-5 sm:w-5 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* Author: full width */}
          <div className="mt-2.5">
            {post.authorUsername && onNavigate ? (
              <button
                type="button"
                data-author-block
                onClick={(e) => {
                  e.stopPropagation();
                  goToProfile();
                }}
                className="inline-flex max-w-full items-center gap-2 rounded-md -mx-1 px-1 py-0.5 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-ihTeal/40 focus:ring-inset text-left"
                aria-label={`View ${post.authorDisplayName}'s profile`}
              >
                <span className="h-7 w-7 flex-shrink-0 overflow-hidden rounded-full bg-slate-100">
                  {post.authorPhotoURL ? (
                    <img src={post.authorPhotoURL} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-slate-500">
                      {post.authorDisplayName?.charAt(0) || '?'}
                    </span>
                  )}
                </span>
                <span className="min-w-0 text-xs text-slate-600">
                  <span className="font-medium text-slate-800">{post.authorDisplayName}</span>
                  <span className="text-slate-400"> · </span>
                  <span className="text-slate-500">@{post.authorUsername}</span>
                </span>
              </button>
            ) : (
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span className="font-medium text-slate-800">{post.authorDisplayName}</span>
                <span className="text-slate-500">@{post.authorUsername}</span>
              </div>
            )}
          </div>

          {/* Body: full width so text uses entire card */}
          {isExpanded ? (
            <>
              <p className="mt-3 w-full text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {post.description}
              </p>
              {post.contentUrl && (
                <p className="mt-3">
                  <a
                    href={post.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 break-all"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {post.contentUrl}
                  </a>
                </p>
              )}
              {post.feedbackPrompt && (
                <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-sm text-amber-950">
                  <span className="font-semibold">They asked: </span>
                  {post.feedbackPrompt}
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 border-t border-slate-100 pt-3">
                <span>{post.commentCount} {post.commentCount === 1 ? 'response' : 'responses'}</span>
                <span className="text-slate-300" aria-hidden>·</span>
                <span>Updated {formatFeedbackTime(post.lastActivityAt)}</span>
              </div>
              {user && post.authorId === user.uid && (
                <div className="mt-3" data-no-collapse>
                  <button
                    type="button"
                    onClick={toggleClosed}
                    disabled={statusBusy}
                    className="text-sm font-medium text-slate-600 hover:text-slate-900 underline disabled:opacity-50"
                  >
                    {post.status === 'open' ? 'Mark as closed' : 'Reopen for feedback'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="mt-2.5 text-sm text-slate-600 line-clamp-2 leading-relaxed">{preview}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
                {post.commentCount === 0 ? (
                  <span>{isOpen ? 'No responses yet' : 'No responses'}</span>
                ) : (
                  <span>
                    {post.commentCount} {post.commentCount === 1 ? 'response' : 'responses'}
                  </span>
                )}
                <span className="text-slate-300" aria-hidden>·</span>
                <span>Updated {formatFeedbackTime(post.lastActivityAt)}</span>
                {lowReplies && (
                  <>
                    <span className="text-slate-300" aria-hidden>·</span>
                    <span className="text-slate-500">Add your take</span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Discussion only: no duplicate post body */}
      {isExpanded && (
        <FeedbackPostExpandedContent
          postId={post.id}
          isOpen={isOpen}
          onNavigate={onNavigate}
          showAllComments={showAllComments}
          onToggleShowAllComments={onToggleShowAllComments}
        />
      )}

      <EditFeedbackPostModal
        post={post}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSuccess={() => setEditModalOpen(false)}
      />
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete this post?"
        body="This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        busy={deleteBusy}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
